/**
 * @fileoverview Customer-facing payment checkout page with Stripe integration.
 *
 * Features:
 * - Displays quote with itemized parts, services, labor, and VAT breakdown
 * - VAT handling: 20% for standard items, 0% for MOT (VAT-exempt)
 * - Stripe Elements for secure card payment
 * - VHC media gallery (photos/videos from vehicle inspection)
 * - A/B testing integration for checkout UX optimization
 *
 * URL: /payment/:token
 *
 * Flow:
 * 1. Load payment data by token
 * 2. Display itemized quote with VAT breakdown
 * 3. Customer reviews and proceeds to payment
 * 4. Stripe PaymentIntent created, card details collected
 * 5. On success, redirect to /payment/:token/success
 *
 * KNOWN ISSUE: Race condition in PaymentIntent creation - see CLAUDE.md
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Checkbox,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  Videocam as VideocamIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  WarningAmber as WarningAmberIcon,
  Groups as GroupsIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  VerifiedUser as VerifiedUserIcon,
  Engineering as EngineeringIcon,
  TrendingDown as TrendingDownIcon,
  Savings as SavingsIcon
} from '@mui/icons-material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import api from '../../services/api';

// Load Stripe only if key is available
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const StripePaymentForm = ({ paymentData, selectedAmount, selectedItems, onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    // Get client secret for payment intent with selected items
    const createPaymentIntent = async () => {
      try {
        console.log('Creating payment intent:', { amount: selectedAmount, items: selectedItems.length });
        const response = await api.post(`/v1/payments/${paymentData.token}/create-intent/`, {
          amount: selectedAmount,
          selected_items: selectedItems
        });
        console.log('Payment intent created:', response.data.client_secret);
        setClientSecret(response.data.client_secret);
      } catch (error) {
        console.error('Failed to create payment intent:', error);
        onPaymentError('Failed to initialize payment');
      }
    };

    if (paymentData.token && selectedAmount > 0) {
      createPaymentIntent();
    }
  }, [paymentData.token, selectedAmount, selectedItems, onPaymentError]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      console.error('Payment submission blocked:', { stripe: !!stripe, elements: !!elements, clientSecret: !!clientSecret });
      onPaymentError('Payment system not ready. Please refresh the page and try again.');
      return;
    }

    setIsProcessing(true);

    const card = elements.getElement(CardElement);

    console.log('Submitting payment to Stripe...');
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: card,
        billing_details: {
          name: paymentData.customer.name,
          email: paymentData.customer.email,
        },
      },
    });

    console.log('Stripe response:', { error, paymentIntent });

    if (error) {
      console.error('Stripe payment error:', error);
      onPaymentError(error.message);
      setIsProcessing(false);
    } else if (paymentIntent.status === 'succeeded') {
      console.log('Payment succeeded, confirming on backend...');
      // Confirm payment on backend with selected items
      try {
        await api.post(`/v1/payments/${paymentData.token}/confirm/`, {
          amount: selectedAmount,
          selected_items: selectedItems
        });
        console.log('Backend confirmation successful');
        onPaymentSuccess();
      } catch (confirmError) {
        console.error('Backend confirmation error:', confirmError);
        onPaymentError('Payment processed but confirmation failed. Please contact support.');
      }
      setIsProcessing(false);
    } else {
      console.warn('Unexpected payment status:', paymentIntent.status);
      onPaymentError(`Payment status: ${paymentIntent.status}. Please contact support.`);
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Details
          </Typography>
          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <CardElement options={cardElementOptions} />
          </Box>
        </CardContent>
      </Card>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={!stripe || isProcessing || selectedAmount === 0}
        sx={{ py: 2 }}
      >
        {isProcessing ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          `Pay £${selectedAmount.toFixed(2)}`
        )}
      </Button>
    </form>
  );
};

const PaymentCheckoutEnhanced = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentState, setPaymentState] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Experiment-related state
  const [experimentStrategies, setExperimentStrategies] = useState([]);
  const [experimentMessages, setExperimentMessages] = useState({});
  const [showCheckboxes, setShowCheckboxes] = useState(true);
  const [deselectionModalOpen, setDeselectionModalOpen] = useState(false);
  const [pendingDeselection, setPendingDeselection] = useState(null);

  // Detect device info for experiment tracking
  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let deviceType = 'desktop';
    let osName = 'Unknown';
    let browserName = 'Unknown';

    // Detect device type
    if (/Mobile|Android|iPhone|iPad|iPod/i.test(ua)) {
      deviceType = /iPad|Tablet/i.test(ua) ? 'tablet' : 'mobile';
    }

    // Detect OS
    if (/Windows/i.test(ua)) osName = 'Windows';
    else if (/Mac OS X|Macintosh/i.test(ua)) osName = 'macOS';
    else if (/iPhone|iPad|iPod/i.test(ua)) osName = 'iOS';
    else if (/Android/i.test(ua)) osName = 'Android';
    else if (/Linux/i.test(ua)) osName = 'Linux';

    // Detect browser
    if (/Chrome/i.test(ua) && !/Edge|Edg/i.test(ua)) browserName = 'Chrome';
    else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browserName = 'Safari';
    else if (/Firefox/i.test(ua)) browserName = 'Firefox';
    else if (/Edge|Edg/i.test(ua)) browserName = 'Edge';

    return { deviceType, osName, browserName };
  };

  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        setLoading(true);
        const deviceInfo = getDeviceInfo();
        const params = new URLSearchParams({
          device_type: deviceInfo.deviceType,
          os_name: deviceInfo.osName,
          browser_name: deviceInfo.browserName,
        });
        const response = await api.get(`/v1/payments/${token}/?${params}`);
        // Allow 'pending' and 'failed' to show checkout form (customer can retry)
        // Other statuses (completed, cancelled, expired) show static message
        if (response.data?.status && !['pending', 'failed'].includes(response.data.status)) {
          setPaymentState(response.data);
          setPaymentData(null);
          setSelectedItems({});
          setError(null);
          return;
        }
        setPaymentData({ ...response.data, token });

        // Initialize experiment strategies
        if (response.data.experiment) {
          const strategies = response.data.experiment.strategies || [];
          setExperimentStrategies(strategies);
          setExperimentMessages({
            socialProof: response.data.experiment.social_proof_message,
            urgencyExpiry: response.data.experiment.urgency_messages?.expiry,
            urgencyWarning: response.data.experiment.urgency_messages?.warning,
            priorityWarnings: response.data.experiment.priority_warnings || {},
            isPersonalized: response.data.experiment.is_personalized || false,
            // New strategies
            anchoring: response.data.experiment.anchoring || {},
            lossAversion: response.data.experiment.loss_aversion || {},
            guarantee: response.data.experiment.guarantee,
            technician: response.data.experiment.technician,
          });

          // Apply hide_checkboxes strategy
          if (strategies.includes('hide_checkboxes')) {
            setShowCheckboxes(false);
          }
        }

        // Initialize all items as selected by default
        const initialSelection = {};
        response.data.items.forEach((item, index) => {
          initialSelection[index] = true;
        });
        setSelectedItems(initialSelection);

        setError(null);
      } catch (err) {
        setError(err.message || 'Payment not found or expired');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPaymentData();
    }
  }, [token]);

  const handlePaymentSuccess = () => {
    navigate(`/payment/${token}/success`);
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  // Track experiment interactions
  const trackInteraction = async (type) => {
    try {
      await api.post(`/v1/payments/${token}/track/`, { type });
    } catch (e) {
      console.log('Failed to track interaction:', e);
    }
  };

  const handleItemToggle = (index) => {
    const isCurrentlySelected = selectedItems[index];

    // If deselecting and deselect_friction strategy is active, show confirmation modal
    if (isCurrentlySelected && experimentStrategies.includes('deselect_friction')) {
      setPendingDeselection(index);
      setDeselectionModalOpen(true);
      return;
    }

    // Normal toggle
    setSelectedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
    trackInteraction('item_toggle');
  };

  // Confirm deselection from modal
  const confirmDeselection = () => {
    if (pendingDeselection !== null) {
      setSelectedItems(prev => ({
        ...prev,
        [pendingDeselection]: false
      }));
      trackInteraction('item_toggle');
    }
    setDeselectionModalOpen(false);
    setPendingDeselection(null);
  };

  // Cancel deselection
  const cancelDeselection = () => {
    setDeselectionModalOpen(false);
    setPendingDeselection(null);
  };

  const VAT_RATE = 0.20;

  const calculateSelectedSubtotal = () => {
    if (!paymentData || !paymentData.items || !Array.isArray(paymentData.items)) {
      return 0;
    }

    // Only count selected items (parts + labor)
    const selectedItemsTotal = paymentData.items.reduce((total, item, index) => {
      if (selectedItems[index]) {
        const itemTotal = (parseFloat(item.price || 0) + parseFloat(item.labor_cost || 0)) * (item.quantity || 1);
        return total + itemTotal;
      }
      return total;
    }, 0);

    // Add services total (always included for first job requests)
    const servicesTotal = paymentData.services && Array.isArray(paymentData.services)
      ? paymentData.services.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0)
      : 0;

    return selectedItemsTotal + servicesTotal;
  };

  // Calculate services total (for display)
  const calculateServicesTotal = () => {
    if (!paymentData || !paymentData.services || !Array.isArray(paymentData.services)) {
      return 0;
    }
    return paymentData.services.reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
  };

  // Calculate VAT-exempt services total (e.g., MOT)
  const calculateVatExemptServicesTotal = () => {
    if (!paymentData || !paymentData.services || !Array.isArray(paymentData.services)) {
      return 0;
    }
    return paymentData.services
      .filter(service => service.is_vat_exempt)
      .reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
  };

  // Calculate VATable services total (non-MOT services)
  const calculateVatableServicesTotal = () => {
    if (!paymentData || !paymentData.services || !Array.isArray(paymentData.services)) {
      return 0;
    }
    return paymentData.services
      .filter(service => !service.is_vat_exempt)
      .reduce((sum, service) => sum + (parseFloat(service.price) || 0), 0);
  };

  const calculateVatAmount = () => {
    // VAT only applies to parts + labor
    // Services already include VAT (BMG prices are VAT-inclusive)
    // MOT is VAT-exempt in UK
    if (!paymentData || !paymentData.items || !Array.isArray(paymentData.items)) {
      return 0;
    }

    // Parts + labor from selected items only
    const selectedItemsTotal = paymentData.items.reduce((total, item, index) => {
      if (selectedItems[index]) {
        const itemTotal = (parseFloat(item.price || 0) + parseFloat(item.labor_cost || 0)) * (item.quantity || 1);
        return total + itemTotal;
      }
      return total;
    }, 0);

    // Services already include VAT, so don't add VAT again
    return selectedItemsTotal * VAT_RATE;
  };

  const calculateSelectedTotal = () => {
    const subtotal = calculateSelectedSubtotal();
    const vat = calculateVatAmount();  // Use the VAT function that excludes MOT
    const total = subtotal + vat;
    console.log('[PaymentCheckout] Total calculated:', { subtotal, vat, total, selectedItems });
    return total;
  };

  const calculateSelectedPartsTotal = () => {
    if (!paymentData || !paymentData.items || !Array.isArray(paymentData.items)) return 0;

    // Only count selected items
    return paymentData.items.reduce((total, item, index) => {
      if (selectedItems[index]) {
        return total + ((item.price || 0) * (item.quantity || 1));
      }
      return total;
    }, 0);
  };

  const calculateSelectedLaborTotal = () => {
    if (!paymentData || !paymentData.items || !Array.isArray(paymentData.items)) return 0;

    // Calculate labor cost for selected items
    return paymentData.items.reduce((total, item, index) => {
      if (selectedItems[index]) {
        return total + ((item.labor_cost || 0) * (item.quantity || 1));
      }
      return total;
    }, 0);
  };

  const getSelectedItems = () => {
    if (!paymentData || !paymentData.items || !Array.isArray(paymentData.items)) return [];

    // Return only selected items
    return paymentData.items.filter((item, index) => selectedItems[index]);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          Go to Homepage
        </Button>
      </Container>
    );
  }

  if (paymentState) {
    const status = paymentState.status || 'completed';
    const statusCopy = {
      completed: {
        title: 'Payment already completed',
        body: 'This payment link has already been used. If you need a receipt or have questions, please contact the garage.'
      },
      processing: {
        title: 'Payment is processing',
        body: 'Your payment is currently processing. You’ll receive a confirmation once it is complete.'
      },
      failed: {
        title: 'Payment failed',
        body: 'This payment attempt did not complete. Please contact the garage if you need assistance.'
      },
      cancelled: {
        title: 'Quote Updated',
        body: 'This quote has been updated. Please check your email for a new payment link with the revised pricing.'
      },
      refunded: {
        title: 'Payment refunded',
        body: 'This payment was refunded. If you have questions, please contact the garage.'
      },
      expired: {
        title: 'Payment link expired',
        body: 'This payment link has expired. Please contact the garage for a new link.'
      }
    }[status] || {
      title: 'Payment not available',
      body: 'This payment link is no longer active. Please contact the garage for assistance.'
    };

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          {paymentState.organization?.logo_url && (
            <Box sx={{ mb: 2 }}>
              <img
                src={paymentState.organization.logo_url}
                alt={paymentState.organization.name || 'Garage'}
                style={{ maxWidth: 220, height: 'auto' }}
              />
            </Box>
          )}
          <Typography variant="h4" gutterBottom>
            {statusCopy.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {statusCopy.body}
          </Typography>
          {paymentState.organization?.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {paymentState.organization.name}
              {paymentState.organization.phone ? ` • ${paymentState.organization.phone}` : ''}
            </Typography>
          )}
          <Button variant="outlined" onClick={() => navigate('/')}>
            Go to Homepage
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          {paymentData.organization?.logo_url && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <img
                src={paymentData.organization.logo_url}
                alt={paymentData.organization.name || 'Garage'}
                style={{ maxWidth: 220, height: 'auto' }}
              />
            </Box>
          )}
          <Typography variant="h5" gutterBottom align="center">
            {paymentData.organization?.name || 'Go Ops'}
          </Typography>
          {(paymentData.organization?.location || paymentData.organization?.address || paymentData.organization?.phone) && (
            <Typography variant="body2" color="text.secondary" align="center">
              {paymentData.organization?.location || paymentData.organization?.address}
              {paymentData.organization?.phone ? ` • ${paymentData.organization.phone}` : ''}
            </Typography>
          )}
        </Box>

        {/* Previous payment failure warning */}
        {paymentData.status === 'failed' && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="warning">
              <strong>Your previous payment attempt was unsuccessful.</strong>
              <br />
              {paymentData.failure_reason || 'The payment could not be processed.'}
              {paymentData.last_card_brand && paymentData.last_card_last4 && (
                <> (Card: {paymentData.last_card_brand} ****{paymentData.last_card_last4})</>
              )}
              <br />
              Please try again with a different card or payment method.
            </Alert>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Alert severity="success">
            <strong>Thank you for choosing {paymentData.organization?.name || 'KIS Autocentres'}!</strong>
            <br />
            Your vehicle assessment is complete. Please review and select the items you'd like to approve for payment below.
          </Alert>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Left Column - Service Details & Items */}
          <Grid item xs={12} lg={8}>
            {/* Service Details */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Details
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Customer:</strong> {paymentData.customer.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Email:</strong> {paymentData.customer.email}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Vehicle:</strong> {paymentData.booking.vehicle.make} {paymentData.booking.vehicle.model} ({paymentData.booking.vehicle.registration})
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Service Date:</strong> {paymentData.booking.booking_date} {paymentData.booking.time_of_day}
                </Typography>
              </CardContent>
            </Card>

            {/* Service Notes */}
            {(paymentData.service_notes?.technician_notes || paymentData.service_notes?.admin_notes) && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Service Notes
                  </Typography>
                  {paymentData.service_notes?.technician_notes && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Technician:</strong> {paymentData.service_notes.technician_notes}
                    </Typography>
                  )}
                  {paymentData.service_notes?.admin_notes && (
                    <Typography variant="body2">
                      <strong>Service Advisor:</strong> {paymentData.service_notes.admin_notes}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Health Check Video */}
            {paymentData.health_check_videos && paymentData.health_check_videos.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <VideocamIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 24 }} />
                    Health Check Video
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Our technician recorded this video during your vehicle inspection to show the items that require attention.
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    {paymentData.health_check_videos.map(video => {
                      // Prepend backend URL if the file_url is relative
                      const videoUrl = video.file_url.startsWith('http')
                        ? video.file_url
                        : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${video.file_url}`.replace('/api', '');

                      return (
                        <Box
                          key={video.id}
                          sx={{
                            width: '100%',
                            maxWidth: 400,
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden',
                            backgroundColor: '#000'
                          }}
                        >
                          <VideoPlayer
                            video={video}
                            videoUrl={videoUrl}
                          />
                          {video.caption && (
                            <Typography variant="caption" sx={{ p: 1, display: 'block', backgroundColor: '#fff' }}>
                              {video.caption}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Health Check Images */}
            {paymentData.health_check_images && paymentData.health_check_images.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <ImageIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 24 }} />
                    Health Check Images
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Our technician captured these images during your vehicle inspection.
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    {paymentData.health_check_images.map(image => {
                      const imageUrl = image.file_url.startsWith('http')
                        ? image.file_url
                        : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${image.file_url}`.replace('/api', '');

                      return (
                        <Box
                          key={image.id}
                          sx={{
                            width: 200,
                            height: 150,
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': {
                              boxShadow: 2,
                            }
                          }}
                          onClick={() => {
                            setSelectedImage(imageUrl);
                            setImageViewerOpen(true);
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={image.caption || 'Health check image'}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Services Included (for first job requests) */}
            {paymentData.services && paymentData.services.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Services Included
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    These services are included with your booking
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Service</TableCell>
                          <TableCell align="right">Price (ex VAT)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paymentData.services.map((service, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2">
                                🔧 {service.name || 'Service'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {service.price > 0 ? `£${service.price.toFixed(2)}` : 'Included'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Experiment: Social Proof Banner */}
            {experimentStrategies.includes('social_proof') && (
              <Alert
                severity="info"
                icon={<GroupsIcon />}
                sx={{ mb: 2, bgcolor: '#e3f2fd' }}
              >
                <Typography variant="body2">
                  <strong>{experimentMessages.socialProof || '85% of customers choose to complete all recommended repairs'}</strong>
                </Typography>
              </Alert>
            )}

            {/* Experiment: Urgency Messaging Banner */}
            {experimentStrategies.includes('urgency_messaging') && (
              <Alert
                severity="warning"
                icon={<ScheduleIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  <strong>{experimentMessages.urgencyExpiry || 'These prices are valid for 7 days'}</strong>
                  {experimentMessages.urgencyWarning && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {experimentMessages.urgencyWarning}
                    </Typography>
                  )}
                </Typography>
              </Alert>
            )}

            {/* Experiment: Guarantee Badge */}
            {experimentStrategies.includes('guarantee_badge') && (
              <Alert
                severity="success"
                icon={<VerifiedUserIcon />}
                sx={{ mb: 2, bgcolor: '#e8f5e9' }}
              >
                <Typography variant="body2">
                  <strong>{experimentMessages.guarantee || '12-month parts warranty included'}</strong>
                </Typography>
              </Alert>
            )}

            {/* Experiment: Technician Credibility */}
            {experimentStrategies.includes('technician_credibility') && experimentMessages.technician && (
              <Alert
                severity="info"
                icon={<EngineeringIcon />}
                sx={{ mb: 2, bgcolor: '#fff3e0' }}
              >
                <Typography variant="body2">
                  <strong>Inspected by {experimentMessages.technician.name}</strong>
                  {experimentMessages.technician.years > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {experimentMessages.technician.years} years of professional experience
                    </Typography>
                  )}
                </Typography>
              </Alert>
            )}

            {/* Items List */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">
                Additional Parts Required
              </Typography>
              {/* Experiment: Hide/Show Checkboxes */}
              {!showCheckboxes && (
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setShowCheckboxes(true)}
                  startIcon={<EditIcon />}
                  size="small"
                >
                  Customize selection
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You’re in control — select the items you want to approve now, and you can leave the rest for later.
              Safety‑critical items are marked as high priority.
            </Typography>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {showCheckboxes && (
                      <TableCell padding="checkbox">
                        <Typography variant="caption">Select</Typography>
                      </TableCell>
                    )}
                    <TableCell>Priority</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Part Number</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Labor Cost</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentData.items.map((item, index) => (
                    <React.Fragment key={index}>
                      <TableRow
                        sx={{
                          opacity: selectedItems[index] ? 1 : 0.5,
                          transition: 'opacity 0.2s'
                        }}
                      >
                        {showCheckboxes && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedItems[index] || false}
                              onChange={() => handleItemToggle(index)}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={`${getPriorityIcon(item.priority)} ${item.priority.toUpperCase()}`}
                            color={getPriorityColor(item.priority)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.part_number || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">£{item.price.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {item.labor_cost > 0 ? `£${item.labor_cost.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell align="right">£{item.total.toFixed(2)}</TableCell>
                      </TableRow>
                      {/* Experiment: Priority Warnings */}
                      {experimentStrategies.includes('priority_warnings') && item.priority === 'high' && (
                        <TableRow>
                          <TableCell colSpan={showCheckboxes ? 8 : 7} sx={{ pt: 0, pb: 1, borderBottom: 'none' }}>
                            <Alert severity="warning" sx={{ py: 0.5 }} icon={<WarningAmberIcon fontSize="small" />}>
                              <Typography variant="caption">
                                <strong>Safety Notice:</strong> {experimentMessages.priorityWarnings?.[item.id] || 'This item affects vehicle safety. Our technician strongly recommends addressing this issue.'}
                              </Typography>
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Experiment: Anchoring - Dealer vs Independent */}
                      {experimentStrategies.includes('anchoring') && experimentMessages.anchoring?.[item.id] && (
                        <TableRow>
                          <TableCell colSpan={showCheckboxes ? 8 : 7} sx={{ pt: 0, pb: 1, borderBottom: 'none' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                              <SavingsIcon fontSize="small" color="success" />
                              <Typography variant="caption" color="success.main">
                                <strong>Independent: £{experimentMessages.anchoring[item.id].independent.toFixed(2)}</strong>
                                <Typography component="span" variant="caption" sx={{ mx: 1, color: 'text.secondary' }}>vs</Typography>
                                <Typography component="span" variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                  Dealer: £{experimentMessages.anchoring[item.id].dealer.toFixed(2)}
                                </Typography>
                                <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                                  (Save £{experimentMessages.anchoring[item.id].savings.toFixed(2)})
                                </Typography>
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Experiment: Loss Aversion - Fix Now vs Later */}
                      {experimentStrategies.includes('loss_aversion') && experimentMessages.lossAversion?.[item.id] && (
                        <TableRow>
                          <TableCell colSpan={showCheckboxes ? 8 : 7} sx={{ pt: 0, pb: 1, borderBottom: 'none' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                              <TrendingDownIcon fontSize="small" color="warning" />
                              <Typography variant="caption" color="warning.main">
                                <strong>Fix now: £{experimentMessages.lossAversion[item.id].now.toFixed(2)}</strong>
                                <Typography component="span" variant="caption" sx={{ mx: 1, color: 'text.secondary' }}>vs</Typography>
                                <Typography component="span" variant="caption" color="error">
                                  Fix later: £{experimentMessages.lossAversion[item.id].later_estimate.toFixed(2)}+
                                </Typography>
                                {experimentMessages.lossAversion[item.id].message && (
                                  <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                                    — {experimentMessages.lossAversion[item.id].message}
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Right Column - Payment Methods & Summary */}
          <Grid item xs={12} lg={4}>
            {/* Payment Summary - Grouped by VAT Rate */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Summary
                </Typography>

                {/* STANDARD-RATED SECTION (20% VAT) */}
                {(calculateVatableServicesTotal() > 0 || calculateSelectedPartsTotal() > 0 || calculateSelectedLaborTotal() > 0) && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box display="flex" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mr: 1 }}>
                        SERVICES & PARTS
                      </Typography>
                      <Chip
                        label="20% VAT"
                        size="small"
                        sx={{
                          bgcolor: '#4caf50',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                    </Box>

                    {/* VATable Services (e.g., General Repair, Sensor work) */}
                    {paymentData.services && paymentData.services.filter(s => !s.is_vat_exempt && s.price > 0).map((service, index) => (
                      <Box key={`service-${index}`} display="flex" justifyContent="space-between" sx={{ mb: 0.5, pl: 1 }}>
                        <Typography variant="body2">{service.name}</Typography>
                        <Typography variant="body2">£{service.price.toFixed(2)}</Typography>
                      </Box>
                    ))}

                    {/* Labour */}
                    {calculateSelectedLaborTotal() > 0 && (
                      <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5, pl: 1 }}>
                        <Typography variant="body2">Labour</Typography>
                        <Typography variant="body2">£{calculateSelectedLaborTotal().toFixed(2)}</Typography>
                      </Box>
                    )}

                    {/* Parts */}
                    {calculateSelectedPartsTotal() > 0 && (
                      <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5, pl: 1 }}>
                        <Typography variant="body2">Parts</Typography>
                        <Typography variant="body2">£{calculateSelectedPartsTotal().toFixed(2)}</Typography>
                      </Box>
                    )}

                    {/* Section Subtotal */}
                    <Box display="flex" justifyContent="space-between" sx={{ mt: 1, pt: 1, borderTop: '1px solid #ddd' }}>
                      <Typography variant="body2" color="text.secondary">Subtotal (ex VAT)</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        £{(calculateVatableServicesTotal() + calculateSelectedPartsTotal() + calculateSelectedLaborTotal()).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* ZERO-RATED SECTION (0% VAT - MOT) */}
                {calculateVatExemptServicesTotal() > 0 && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box display="flex" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mr: 1 }}>
                        MOT TEST
                      </Typography>
                      <Chip
                        label="0% VAT"
                        size="small"
                        sx={{
                          bgcolor: '#9e9e9e',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                    </Box>

                    {/* MOT Services */}
                    {paymentData.services && paymentData.services.filter(s => s.is_vat_exempt && s.price > 0).map((service, index) => (
                      <Box key={`mot-${index}`} display="flex" justifyContent="space-between" sx={{ mb: 0.5, pl: 1 }}>
                        <Typography variant="body2">{service.name}</Typography>
                        <Typography variant="body2">£{service.price.toFixed(2)}</Typography>
                      </Box>
                    ))}

                    {/* Section Subtotal */}
                    <Box display="flex" justifyContent="space-between" sx={{ mt: 1, pt: 1, borderTop: '1px solid #ddd' }}>
                      <Typography variant="body2" color="text.secondary">Subtotal (ex VAT)</Typography>
                      <Typography variant="body2" fontWeight="bold">£{calculateVatExemptServicesTotal().toFixed(2)}</Typography>
                    </Box>
                  </Box>
                )}

                {/* VAT BREAKDOWN */}
                <Box sx={{ mb: 2 }}>
                  {/* Total ex VAT */}
                  <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">Total (ex VAT)</Typography>
                    <Typography variant="body2">£{calculateSelectedSubtotal().toFixed(2)}</Typography>
                  </Box>

                  {/* VAT @ 20% */}
                  {(calculateVatableServicesTotal() + calculateSelectedPartsTotal() + calculateSelectedLaborTotal()) > 0 && (
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        VAT @ 20% (on £{(calculateVatableServicesTotal() + calculateSelectedPartsTotal() + calculateSelectedLaborTotal()).toFixed(2)})
                      </Typography>
                      <Typography variant="body2">£{calculateVatAmount().toFixed(2)}</Typography>
                    </Box>
                  )}

                  {/* VAT @ 0% */}
                  {calculateVatExemptServicesTotal() > 0 && (
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        VAT @ 0% (on £{calculateVatExemptServicesTotal().toFixed(2)})
                      </Typography>
                      <Typography variant="body2">£0.00</Typography>
                    </Box>
                  )}
                </Box>

                {/* TOTAL TO PAY */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#00695c',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Total to Pay
                  </Typography>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    £{calculateSelectedTotal().toFixed(2)}
                  </Typography>
                </Box>

                {calculateSelectedTotal() === 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Please select at least one item to proceed with payment.
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Payment Form */}
            {calculateSelectedTotal() > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Payment Details
                </Typography>
                {stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <StripePaymentForm
                      paymentData={paymentData}
                      selectedAmount={calculateSelectedTotal()}
                      selectedItems={getSelectedItems()}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                    />
                  </Elements>
                ) : (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Payment system is not configured. Please contact support.
                  </Alert>
                )}
              </Box>
            )}
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Secure Payment:</strong> Your payment is processed securely through our trusted payment partners.
            After payment, work will resume immediately on your vehicle.
          </Typography>
        </Box>
      </Paper>
      {/* Image Viewer Dialog */}
      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setImageViewerOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Health check"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Experiment: Deselection Confirmation Modal */}
      <Dialog
        open={deselectionModalOpen}
        onClose={cancelDeselection}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Confirm Removal
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove this item from your selection?
          </Typography>
          {pendingDeselection !== null && paymentData?.items?.[pendingDeselection] && (
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2">
                {paymentData.items[pendingDeselection].name}
              </Typography>
              {paymentData.items[pendingDeselection].priority === 'high' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This is a high-priority safety item. Our technician recommends addressing this promptly.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={confirmDeselection} variant="outlined" color="inherit">
            Remove Anyway
          </Button>
          <Button onClick={cancelDeselection} variant="contained" color="primary">
            Keep Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// VideoPlayer component with URL refresh on error
const VideoPlayer = ({ video, videoUrl: initialUrl }) => {
  const [videoUrl, setVideoUrl] = useState(initialUrl);
  const [retryCount, setRetryCount] = useState(0);
  const videoRef = React.useRef(null);

  const refreshVideoUrl = async () => {
    if (retryCount >= 3) {
      console.error('Max retry attempts reached for video:', video.id);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/evhc/media/${video.id}/refresh-url/`);

      if (!response.ok) {
        throw new Error('Failed to refresh video URL');
      }

      const data = await response.json();
      const newUrl = data.file_url.startsWith('http')
        ? data.file_url
        : `${apiUrl}${data.file_url}`.replace('/api', '');

      setVideoUrl(newUrl);
      setRetryCount(prev => prev + 1);

      // Reload video
      if (videoRef.current) {
        videoRef.current.load();
      }

      console.log('Video URL refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh video URL:', error);
      setRetryCount(prev => prev + 1);
    }
  };

  const handleVideoError = (e) => {
    console.error('Video playback error:', e);
    console.error('Video URL:', videoUrl);
    console.error('Error code:', videoRef.current?.error?.code);

    // Attempt to refresh URL on 403 Forbidden or network errors
    if (videoRef.current?.error?.code === 4 || videoRef.current?.error?.code === 2) {
      console.log('Attempting to refresh video URL...');
      refreshVideoUrl();
    }
  };

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      style={{ width: '100%', display: 'block' }}
      controls
      onError={handleVideoError}
    />
  );
};

export default PaymentCheckoutEnhanced;
